import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useCart } from '../contexts/CartContext.tsx';
import { ordersAPI } from '../services/api.ts';
import H2GOLogo from '../components/H2GOLogo.tsx';
import PincodeInput from '../components/PincodeInput.tsx';

const Checkout: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { items, totalPrice, clearCart } = useCart();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [orderError, setOrderError] = useState<string>('');
  const [deliveryInfo, setDeliveryInfo] = useState({
    fullName: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    city: user?.city || '',
    state: '',
    pincode: user?.pincode || '',
    deliveryDate: '',
    deliveryTime: '',
    specialInstructions: ''
  });
  const [paymentInfo, setPaymentInfo] = useState({
    method: 'cod'
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (items.length === 0) {
      navigate('/cart');
      return;
    }
  }, [isAuthenticated, items.length, navigate]);

  useEffect(() => {
    if (user) {
      setDeliveryInfo(prev => ({
        ...prev,
        fullName: user.name || prev.fullName,
        email: user.email || prev.email,
        phone: user.phone || prev.phone,
        address: user.address || prev.address,
        city: user.city || prev.city,
        pincode: user.pincode || prev.pincode
      }));
    }
  }, [user]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!deliveryInfo.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!deliveryInfo.email.trim()) newErrors.email = 'Email is required';
    if (!deliveryInfo.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!deliveryInfo.address.trim()) newErrors.address = 'Address is required';
    if (!deliveryInfo.city.trim()) newErrors.city = 'City is required';
    if (!deliveryInfo.state.trim()) newErrors.state = 'State is required';
    if (!deliveryInfo.pincode.trim()) newErrors.pincode = 'Pincode is required';
    if (!deliveryInfo.deliveryDate) newErrors.deliveryDate = 'Delivery date is required';
    if (!deliveryInfo.deliveryTime) newErrors.deliveryTime = 'Delivery time is required';

    // Server-side validation rules
    if (deliveryInfo.address.trim() && (deliveryInfo.address.trim().length < 10 || deliveryInfo.address.trim().length > 500)) {
      newErrors.address = 'Address must be between 10 and 500 characters';
    }
    if (deliveryInfo.city.trim() && (deliveryInfo.city.trim().length < 2 || deliveryInfo.city.trim().length > 50)) {
      newErrors.city = 'City must be between 2 and 50 characters';
    }
    if (deliveryInfo.pincode.trim() && (deliveryInfo.pincode.trim().length < 6 || deliveryInfo.pincode.trim().length > 10)) {
      newErrors.pincode = 'Pincode must be between 6 and 10 characters';
    }
    if (deliveryInfo.phone.trim() && (deliveryInfo.phone.trim().length < 10 || deliveryInfo.phone.trim().length > 15)) {
      newErrors.phone = 'Phone must be between 10 and 15 characters';
    }
    if (deliveryInfo.specialInstructions.trim() && deliveryInfo.specialInstructions.trim().length > 500) {
      newErrors.specialInstructions = 'Special instructions must not exceed 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Clear specific field errors when user starts typing
  const clearFieldError = useCallback((fieldName: string) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  // Memoized callbacks to prevent infinite re-renders
  const handlePincodeChange = useCallback((pincode: string) => {
    setDeliveryInfo(prev => ({ ...prev, pincode }));
    if (errors.pincode) clearFieldError('pincode');
  }, [errors.pincode, clearFieldError]);

  const handleCityStateChange = useCallback((city: string, state: string) => {
    setDeliveryInfo(prev => ({ ...prev, city, state }));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsProcessing(true);
    setOrderError(''); // Clear any previous errors
    
    try {
      // Prepare order data
      const orderData = {
        items: items.map(item => ({
          productId: item.product.id,
          quantity: item.quantity
        })),
        deliveryAddress: deliveryInfo.address,
        deliveryCity: deliveryInfo.city,
        deliveryPincode: deliveryInfo.pincode,
        deliveryPhone: deliveryInfo.phone,
        deliveryDate: deliveryInfo.deliveryDate,
        deliveryTime: deliveryInfo.deliveryTime,
        notes: deliveryInfo.specialInstructions,
        paymentMethod: paymentInfo.method === 'cod' ? 'cash' : 'online'
      };

      // Create order via API
      const response = await ordersAPI.createOrder(orderData);
      
      if (response.data.success) {
        // Clear cart and redirect to success page
        clearCart();
        const orderNumber = response.data.data.order.orderNumber;
        const orderId = response.data.data.order.id;
        
        // Redirect to success page with order details
        navigate(`/order-success?orderNumber=${orderNumber}&orderId=${orderId}`);
      } else {
        throw new Error(response.data.message || 'Order creation failed');
      }
    } catch (error: any) {
      console.error('Order creation failed:', error);
      
      // Handle validation errors specifically
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const validationErrors = error.response.data.errors;
        const errorMessages = validationErrors.map((err: any) => {
          // Map server field names to user-friendly field names
          const fieldMap: Record<string, string> = {
            'deliveryAddress': 'Address',
            'deliveryCity': 'City',
            'deliveryPincode': 'Pincode',
            'deliveryPhone': 'Phone',
            'deliveryDate': 'Delivery Date',
            'deliveryTime': 'Delivery Time',
            'paymentMethod': 'Payment Method',
            'notes': 'Special Instructions'
          };
          
          const fieldName = fieldMap[err.path] || err.path;
          return `• ${fieldName}: ${err.msg}`;
        });
        
        setOrderError(`Please fix the following issues:\n${errorMessages.join('\n')}`);
      } else if (error.response?.data?.stockIssues && Array.isArray(error.response.data.stockIssues)) {
        // Handle stock issues
        const stockIssues = error.response.data.stockIssues;
        const stockErrorMessages = stockIssues.map((issue: any) => {
          if (issue.issue === 'Insufficient stock') {
            return `• ${issue.productName}: Only ${issue.availableStock} available, but ${issue.requestedQuantity} requested`;
          } else if (issue.issue === 'Product not found or inactive') {
            return `• ${issue.productName}: Product is no longer available`;
          }
          return `• ${issue.productName}: ${issue.issue}`;
        });
        
        setOrderError(`Stock issues detected:\n${stockErrorMessages.join('\n')}\n\nPlease update your cart and try again.`);
      } else {
        // Handle other API errors
        const errorMessage = error.response?.data?.message || error.message || 'Failed to create order. Please try again.';
        setOrderError(errorMessage);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isAuthenticated || items.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Error Banner */}
          {orderError && (
            <div className="mb-8 relative overflow-hidden bg-gradient-to-r from-red-50 via-rose-50 to-pink-50 border border-red-200 rounded-2xl shadow-lg">
              <div className="absolute inset-0 bg-gradient-to-r from-red-400/10 via-rose-400/10 to-pink-400/10"></div>
              <div className="relative p-8">
                {/* Close button */}
                <button
                  onClick={() => setOrderError('')}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                <div className="flex items-start space-x-4">
                  {/* Error Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-rose-500 rounded-full flex items-center justify-center shadow-lg">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      ❌ Order Failed
                    </h3>
                    <p className="text-lg text-gray-700 mb-4">
                      We encountered an issue while processing your order. Please try again.
                    </p>
                    <div className="bg-white/70 rounded-lg p-4 mb-4">
                      <div className="text-red-600 text-sm font-medium">
                        {orderError.includes('\n') ? (
                          <div className="space-y-2">
                            <div className="flex items-start">
                              <svg className="w-4 h-4 mr-2 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
                              </svg>
                              <div className="flex-1">
                                <p className="font-semibold mb-2">Please fix the following issues:</p>
                                <div className="space-y-1">
                                  {orderError.split('\n').slice(1).map((line, index) => (
                                    <p key={index} className="text-sm">{line}</p>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="inline-flex items-center">
                            <svg className="w-4 h-4 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            {orderError}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => setOrderError('')}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-lg hover:from-red-700 hover:to-rose-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Try Again
                      </button>
                      <button
                        onClick={() => navigate('/cart')}
                        className="inline-flex items-center px-6 py-3 bg-white text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 font-semibold border border-gray-300 shadow-sm hover:shadow-md"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
                        </svg>
                        Back to Cart
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-red-400/20 to-rose-400/20 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-pink-400/20 to-rose-400/20 rounded-full translate-y-12 -translate-x-12"></div>
            </div>
          )}
          
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Checkout</h1>

          {!orderError && (
            <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Delivery Information</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input 
                      type="text" 
                      value={deliveryInfo.fullName}
                      onChange={(e) => {
                        setDeliveryInfo(prev => ({ ...prev, fullName: e.target.value }));
                        if (errors.fullName) clearFieldError('fullName');
                      }}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        errors.fullName ? 'border-red-300' : 'border-gray-300'
                      }`} 
                      required 
                    />
                    {errors.fullName && <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input 
                      type="email" 
                      value={deliveryInfo.email}
                      onChange={(e) => {
                        setDeliveryInfo(prev => ({ ...prev, email: e.target.value }));
                        if (errors.email) clearFieldError('email');
                      }}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        errors.email ? 'border-red-300' : 'border-gray-300'
                      }`} 
                      required 
                    />
                    {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                    <input 
                      type="tel" 
                      value={deliveryInfo.phone}
                      onChange={(e) => {
                        setDeliveryInfo(prev => ({ ...prev, phone: e.target.value }));
                        if (errors.phone) clearFieldError('phone');
                      }}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        errors.phone ? 'border-red-300' : 'border-gray-300'
                      }`} 
                      required 
                    />
                    {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pincode *</label>
                    <PincodeInput
                      value={deliveryInfo.pincode}
                      onChange={handlePincodeChange}
                      onCityStateChange={handleCityStateChange}
                      placeholder="Enter 6-digit pincode"
                      className={errors.pincode ? 'border-red-300' : ''}
                      error={errors.pincode}
                      required
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                  <textarea 
                    rows={3} 
                    value={deliveryInfo.address}
                    onChange={(e) => {
                      setDeliveryInfo(prev => ({ ...prev, address: e.target.value }));
                      if (errors.address) clearFieldError('address');
                    }}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.address ? 'border-red-300' : 'border-gray-300'
                    }`} 
                    required 
                  />
                  {errors.address && <p className="mt-1 text-sm text-red-600">{errors.address}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                    <input 
                      type="text" 
                      value={deliveryInfo.city}
                      onChange={(e) => setDeliveryInfo(prev => ({ ...prev, city: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        errors.city ? 'border-red-300' : 'border-gray-300'
                      }`} 
                      required 
                    />
                    {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                    <input 
                      type="text" 
                      value={deliveryInfo.state}
                      onChange={(e) => setDeliveryInfo(prev => ({ ...prev, state: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        errors.state ? 'border-red-300' : 'border-gray-300'
                      }`} 
                      required 
                    />
                    {errors.state && <p className="mt-1 text-sm text-red-600">{errors.state}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Date *</label>
                    <input 
                      type="date" 
                      value={deliveryInfo.deliveryDate}
                      onChange={(e) => setDeliveryInfo(prev => ({ ...prev, deliveryDate: e.target.value }))}
                      min={new Date().toISOString().split('T')[0]} 
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        errors.deliveryDate ? 'border-red-300' : 'border-gray-300'
                      }`} 
                      required 
                    />
                    {errors.deliveryDate && <p className="mt-1 text-sm text-red-600">{errors.deliveryDate}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Time *</label>
                    <select 
                      value={deliveryInfo.deliveryTime}
                      onChange={(e) => setDeliveryInfo(prev => ({ ...prev, deliveryTime: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        errors.deliveryTime ? 'border-red-300' : 'border-gray-300'
                      }`} 
                      required
                    >
                      <option value="">Select time</option>
                      <option value="Morning">Morning (9 AM - 12 PM)</option>
                      <option value="Afternoon">Afternoon (12 PM - 4 PM)</option>
                      <option value="Evening">Evening (4 PM - 8 PM)</option>
                    </select>
                    {errors.deliveryTime && <p className="mt-1 text-sm text-red-600">{errors.deliveryTime}</p>}
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Special Instructions</label>
                  <textarea 
                    rows={3} 
                    value={deliveryInfo.specialInstructions}
                    onChange={(e) => setDeliveryInfo(prev => ({ ...prev, specialInstructions: e.target.value }))}
                    placeholder="Any special delivery instructions (optional)"
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.specialInstructions ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.specialInstructions && <p className="mt-1 text-sm text-red-600">{errors.specialInstructions}</p>}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Method</h2>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input 
                      type="radio" 
                      id="cod" 
                      name="paymentMethod" 
                      value="cod" 
                      checked={paymentInfo.method === 'cod'}
                      onChange={(e) => setPaymentInfo(prev => ({ ...prev, method: e.target.value }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" 
                    />
                    <label htmlFor="cod" className="ml-3 block text-sm font-medium text-gray-700">Cash on Delivery</label>
                  </div>
                  <div className="flex items-center">
                  
                    {/* <input 
                      type="radio" 
                      id="online" 
                      name="paymentMethod" 
                      value="online" 
                      checked={paymentInfo.method === 'online'}
                      onChange={(e) => setPaymentInfo(prev => ({ ...prev, method: e.target.value }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" 
                    />
                    { <label htmlFor="online" className="ml-3 block text-sm font-medium text-gray-700">Online Payment</label> } */}
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-6 sticky top-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>
                
                <div className="space-y-3 mb-6">
                  {items.map((item) => (
                    <div key={item.product.id} className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        {item.product.image ? (
                          <img src={item.product.image} alt={item.product.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <H2GOLogo size="sm" showText={false} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{item.product.name}</p>
                        <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                      </div>
                      <div className="text-sm font-medium text-gray-900">₹{item.product.price * item.quantity}</div>
                    </div>
                  ))}
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">₹{totalPrice}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Delivery Fee</span>
                    <span className="font-medium">₹50</span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex justify-between">
                      <span className="text-lg font-semibold text-gray-900">Total</span>
                      <span className="text-lg font-semibold text-gray-900">₹{totalPrice + 50}</span>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isProcessing}
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? 'Processing Order...' : 'Place Order'}
                </button>

                <div className="mt-4 text-center">
                  <p className="text-xs text-gray-500">By placing this order, you agree to our terms and conditions</p>
                </div>
              </div>
            </div>
          </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Checkout;
